import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import {
    Popover,
    PopoverAnchor,
    PopoverContent,
} from '@rocco/components/popover';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as CloseIcon } from '@rocco/icons/svg/close.svg';
import { ReactComponent as SendIcon } from '@rocco/icons/svg/send.svg';
import {
    ISLAND_GLASS_DARK,
    ISLAND_GLASS_LAYOUT_ID,
} from '~/ui-rocco/pages/detail-shared/magic-island/styles';

import { SelectedItemMedia } from './SelectedItemMedia';
import { SelectedItemsList } from './SelectedItemsList';
import { countLabel } from './bulk-enquiry-copy';
import type { BulkEnquiryItem } from './bulk-enquiry-state';
import { useIsBelowMd } from './use-below-md';

interface BulkEnquireIslandProps {
    items: BulkEnquiryItem[];
    /** The browse page's own kind, so the empty label can name what to add. */
    entityType: 'products' | 'professionals' | 'projects';
    onRemove: (siteTreeID: number) => void;
    onClearAll: () => void;
    onExit: () => void;
    onSubmit: () => void;
}

const POP = { type: 'spring', stiffness: 520, damping: 24, mass: 1 } as const;

/** Product circles shown in the pill; the rest collapse into a "+N" circle. */
const MAX_CIRCLES = 6;

/**
 * Below `md` the island has to fit a 414px viewport, so the pill shows fewer
 * circles before collapsing into the "+N" count — keeping the exit and send
 * targets clear of the screen edges.
 */
const MAX_CIRCLES_MOBILE = 3;

/** Empty slots hinting where the first picks will land. */
const EMPTY_SLOTS = 3;

/**
 * Only mouse and pen drive the hover open/close (and its grace timer). An
 * allowlist rather than excluding `touch`: synthetic or unusual pointers report
 * an empty `pointerType`, and treating those as hover would arm the close
 * timer against a tap-opened preview.
 */
const isHoverPointer = (pointerType: string): boolean =>
    pointerType === 'mouse' || pointerType === 'pen';

// Grace period so the cursor can travel from the pill to the preview panel
// without it closing underneath them.
const HOVER_CLOSE_DELAY_MS = 160;

/**
 * The Bulk Enquire island surface (mode on), shaped like Apple's AirPods colour
 * picker: a floating label and a glass pill holding one circular thumbnail per
 * selected product (overflowing into a "+N" circle), with the send button inset
 * at the pill's trailing edge — the same treatment as the island's CTA pill — and
 * an exit control outside on the left.
 *
 * The circles are display-only: hovering (or tapping) the pill opens the
 * removable {@link SelectedItemsList}, which is also where clear-all lives. The
 * label hides while that panel is open, since they share the space above.
 */
export const BulkEnquireIsland = ({
    items,
    entityType,
    onRemove,
    onClearAll,
    onExit,
    onSubmit,
}: BulkEnquireIslandProps) => {
    const reduce = useReducedMotion() ?? false;
    const pop = reduce ? { duration: 0 } : POP;
    const count = items.length;
    const hasSelection = count > 0;

    const belowMd = useIsBelowMd();
    const shown = items.slice(0, belowMd ? MAX_CIRCLES_MOBILE : MAX_CIRCLES);
    const overflow = count - shown.length;

    const [previewOpen, setPreviewOpen] = useState(false);
    const closeTimer = useRef(0);
    const pillRef = useRef<HTMLDivElement>(null);

    const cancelClose = useCallback(() => {
        if (closeTimer.current) {
            window.clearTimeout(closeTimer.current);
            closeTimer.current = 0;
        }
    }, []);

    const scheduleClose = useCallback(() => {
        cancelClose();
        closeTimer.current = window.setTimeout(
            () => setPreviewOpen(false),
            HOVER_CLOSE_DELAY_MS,
        );
    }, [cancelClose]);

    const openPreview = useCallback(() => {
        cancelClose();
        setPreviewOpen(true);
    }, [cancelClose]);

    const togglePreview = useCallback(() => {
        cancelClose();
        setPreviewOpen((open) => !open);
    }, [cancelClose]);

    // Touch has no hover, so the pointer type of the last press decides the
    // gesture: a mouse/pen click only opens the preview (hover has usually
    // opened it already, and a toggle would fight that); anything else — touch
    // included — toggles it. Below md there is no hover-preview at all: a tap
    // on the circles goes straight to the enquiry sheet's review step, where
    // the list can actually be worked with.
    const lastPointerType = useRef<string>('mouse');
    const handleCirclesActivate = useCallback(
        (event: React.MouseEvent) => {
            if (belowMd) {
                onSubmit();
                return;
            }
            // Keyboard activation (Enter/Space) fires a click with no preceding
            // pointerdown, so lastPointerType is stale — detect it (detail 0,
            // set only by real pointer clicks) and toggle, so the keyboard can
            // close the preview it opened rather than being stuck on open.
            const isKeyboard = event.detail === 0;
            if (!isKeyboard && isHoverPointer(lastPointerType.current))
                openPreview();
            else togglePreview();
        },
        [belowMd, onSubmit, togglePreview, openPreview],
    );

    useEffect(() => () => cancelClose(), [cancelClose]);
    // Nothing to preview once the selection empties.
    useEffect(() => {
        if (!hasSelection) setPreviewOpen(false);
    }, [hasSelection]);

    const circleClass =
        'size-10 shrink-0 overflow-hidden rounded-full bg-white/15 ring-1 ring-white/25';

    // Empty, name what belongs here (the page already knows); once they've
    // started, just nudge them to keep going.
    const label = hasSelection
        ? 'Add more to your enquiry list'
        : `Add ${entityType} to your enquiry list`;
    const selected = countLabel(items);

    return (
        // Below md the main pill (selection + send + label) centres on the
        // VIEWPORT with the exit cross a constant gap-2 to its left: an
        // invisible spacer mirrors the cross on the right, so flex centring
        // keeps the pill exactly centred while the cross drifts left as the
        // pill grows. Desktop keeps the one centred group (spacer hidden).
        <div className="pointer-events-auto flex items-center gap-2 max-md:w-screen max-md:justify-center">
            <motion.button
                type="button"
                onClick={onExit}
                aria-label="Exit bulk enquiry"
                initial={reduce ? false : { opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={pop}
                className={cn(
                    ISLAND_GLASS_DARK,
                    'flex size-14 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-black/70 active:bg-black/70',
                )}
            >
                <CloseIcon className="size-4 fill-current" />
            </motion.button>

            {/* Anchors the floating label to the pill (absolute, so hiding it
            never nudges the island). The label animates on first mount too —
            it rides the glass pill's morph into bulk mode. */}
            <div className="relative">
                <AnimatePresence>
                    {!previewOpen ? (
                        <motion.span
                            key="label"
                            initial={reduce ? false : { opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={
                                reduce ? { opacity: 0 } : { opacity: 0, y: 6 }
                            }
                            transition={pop}
                            className="absolute bottom-full left-1/2 mb-3 -translate-x-1/2 rounded-full bg-white px-3 py-1.5 font-sans text-sm font-medium whitespace-nowrap text-[#181818] shadow-[0_6px_20px_rgba(0,0,0,0.18)]"
                        >
                            {label}
                        </motion.span>
                    ) : null}
                </AnimatePresence>

                <Popover open={previewOpen} onOpenChange={setPreviewOpen}>
                    <PopoverAnchor asChild>
                        {/* The pill itself is not a control — it holds two: the
                        circles (which open the list) and the inset send button.
                        Hovering anywhere on it opens the list. */}
                        <motion.div
                            ref={pillRef}
                            layout={!reduce}
                            layoutId={
                                reduce ? undefined : ISLAND_GLASS_LAYOUT_ID
                            }
                            transition={pop}
                            // Hover opens/closes for mouse and pen only; touch
                            // drives the sheet by tap (below), so its
                            // hover-close timer must never arm for it. Below
                            // md the preview doesn't exist — taps open the
                            // enquiry sheet instead.
                            onPointerEnter={(event) => {
                                if (belowMd) return;
                                if (!isHoverPointer(event.pointerType)) return;
                                if (hasSelection) openPreview();
                            }}
                            onPointerLeave={(event) => {
                                if (belowMd) return;
                                if (!isHoverPointer(event.pointerType)) return;
                                scheduleClose();
                            }}
                            className={cn(
                                ISLAND_GLASS_DARK,
                                'flex h-14 items-center gap-1.5 rounded-full p-2',
                            )}
                        >
                            <button
                                type="button"
                                // Record the pointer type first so the click can
                                // tell a tap (toggle) from a mouse/pen click
                                // (open only).
                                onPointerDown={(event) => {
                                    lastPointerType.current = event.pointerType;
                                }}
                                onClick={
                                    hasSelection
                                        ? handleCirclesActivate
                                        : undefined
                                }
                                disabled={!hasSelection}
                                aria-haspopup="dialog"
                                aria-expanded={previewOpen}
                                aria-label={
                                    hasSelection
                                        ? `${selected} in your enquiry list — view`
                                        : 'Nothing in your enquiry list yet'
                                }
                                // The circles are 40px; below md the button box
                                // stretches to 44px (still inside the 56px pill)
                                // so the tap target meets the touch minimum
                                // without visually bloating the pill.
                                className="flex items-center gap-1.5 rounded-full max-md:h-11"
                            >
                                {hasSelection ? (
                                    <AnimatePresence
                                        initial={false}
                                        mode="popLayout"
                                    >
                                        {shown.map((item) => (
                                            <motion.span
                                                key={item.siteTreeID}
                                                layout={!reduce}
                                                initial={
                                                    reduce
                                                        ? false
                                                        : {
                                                              opacity: 0,
                                                              scale: 0.4,
                                                          }
                                                }
                                                animate={{
                                                    opacity: 1,
                                                    scale: 1,
                                                }}
                                                exit={
                                                    reduce
                                                        ? { opacity: 0 }
                                                        : {
                                                              opacity: 0,
                                                              scale: 0.4,
                                                          }
                                                }
                                                transition={pop}
                                                className="shrink-0"
                                            >
                                                <SelectedItemMedia
                                                    item={item}
                                                    className={circleClass}
                                                />
                                            </motion.span>
                                        ))}
                                        {overflow > 0 ? (
                                            <motion.span
                                                key="overflow"
                                                layout={!reduce}
                                                initial={
                                                    reduce
                                                        ? false
                                                        : {
                                                              opacity: 0,
                                                              scale: 0.4,
                                                          }
                                                }
                                                animate={{
                                                    opacity: 1,
                                                    scale: 1,
                                                }}
                                                exit={
                                                    reduce
                                                        ? { opacity: 0 }
                                                        : {
                                                              opacity: 0,
                                                              scale: 0.4,
                                                          }
                                                }
                                                transition={pop}
                                                className={cn(
                                                    circleClass,
                                                    'flex items-center justify-center font-sans text-sm font-semibold',
                                                )}
                                            >
                                                {`+${overflow}`}
                                            </motion.span>
                                        ) : null}
                                    </AnimatePresence>
                                ) : (
                                    Array.from({ length: EMPTY_SLOTS }).map(
                                        (_, index) => (
                                            <span
                                                key={index}
                                                className="size-10 shrink-0 rounded-full border border-dashed border-white/25"
                                            />
                                        ),
                                    )
                                )}
                            </button>

                            {/* Inset send button — the same trailing circular
                            control the island's CTA pill uses. */}
                            <AnimatePresence initial={false}>
                                {hasSelection ? (
                                    <motion.button
                                        key="send"
                                        type="button"
                                        onClick={onSubmit}
                                        layout={!reduce}
                                        initial={
                                            reduce
                                                ? false
                                                : { opacity: 0, scale: 0.6 }
                                        }
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={
                                            reduce
                                                ? { opacity: 0 }
                                                : { opacity: 0, scale: 0.6 }
                                        }
                                        transition={pop}
                                        aria-label={`Send enquiry for ${selected}`}
                                        className="ml-1 flex size-10 shrink-0 items-center justify-center rounded-full bg-white text-[#181818] transition-transform duration-200 ease-out active:scale-95 max-md:size-11"
                                    >
                                        <SendIcon className="size-4 fill-current" />
                                    </motion.button>
                                ) : null}
                            </AnimatePresence>
                        </motion.div>
                    </PopoverAnchor>

                    <PopoverContent
                        side="top"
                        align="center"
                        sideOffset={12}
                        background="white"
                        paddings="small"
                        // Fixed, mobile-safe width. px (not rem) so it doesn't
                        // shrink with the responsive root font, and inline so it
                        // beats the variant's `w-fit`.
                        style={{ width: 'min(380px, calc(100vw - 32px))' }}
                        // Hover-open shouldn't yank focus into the panel; Escape
                        // and outside-click still close it.
                        onOpenAutoFocus={(event) => event.preventDefault()}
                        // A tap on the pill toggles via its own handler; stop
                        // Radix from also dismissing here, or the two fight and
                        // it reopens instead of closing.
                        onPointerDownOutside={(event) => {
                            const target = event.detail.originalEvent
                                .target as Node | null;
                            if (target && pillRef.current?.contains(target)) {
                                event.preventDefault();
                            }
                        }}
                        onPointerEnter={(event) => {
                            if (!isHoverPointer(event.pointerType)) return;
                            cancelClose();
                        }}
                        onPointerLeave={(event) => {
                            if (!isHoverPointer(event.pointerType)) return;
                            scheduleClose();
                        }}
                    >
                        <div className="flex items-center justify-between gap-4 px-1 pb-2">
                            <p className="text-sm font-semibold text-[#181818]">
                                Your enquiry list
                            </p>
                            <button
                                type="button"
                                onClick={onClearAll}
                                className="rounded text-xs font-medium text-gray-500 transition-colors hover:text-gray-800 max-md:min-h-11"
                            >
                                Clear all
                            </button>
                        </div>
                        <SelectedItemsList items={items} onRemove={onRemove} />
                    </PopoverContent>
                </Popover>
            </div>
            {/* Mirror of the exit cross: balances flex centring below md so
                the pill sits exactly centred with a constant gap to the cross. */}
            <span aria-hidden="true" className="size-14 shrink-0 md:hidden" />
        </div>
    );
};
